import React, { useContext, useMemo, useState } from "react";
import { Button, DatePicker, Input, Select, Spin } from "antd";
import { UserContext } from "../App";
import { useInfringementReview } from "../hooks/useInfringementReview";
import { infringementDefaultFilters, useInfringementStream } from "../hooks/useInfringementStream";
import { Endpoints } from "../constants/endpoint.constant";
import { DefaultOrigins } from "../constants/origins.constant";
import { getRegionalOriginIds, isRegionalManagerRole } from "../constants/general.constant";
import { httpClient } from "../api/httpClient";
import { InfringementDetailModal } from "./infringement_detail_modal.component";
import { InfringementActionLogPanel } from "./infringement_action_log_panel.component";
import { InfringementTable } from "./infringement_table.component";
import { OfflinePanel } from "./offline_panel.component";
import { SpeedInfringementSettingsPanel } from "./speed_infringement_settings_panel.component";
import { SpeedingPanel } from "./speeding_panel.component";
import { toast } from "react-toastify";

const SummaryCard = ({ label, value, accent = "" }) => {
  return <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">{label}</div>
    <div className={`mt-2 text-[1.5rem] font-semibold text-[#161311] ${accent}`}>{value}</div>
  </div>;
};

export const InfringementsDashboard = ({
  tripId = null,
  title = "Infringements Dashboard",
  showFilters = true,
  limitCount = 250,
}) => {
  const { user } = useContext(UserContext);
  const [filters, setFilters] = useState(infringementDefaultFilters);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionSavingId, setActionSavingId] = useState(null);
  const isRegionalManager = isRegionalManagerRole(user);
  const regionalOriginIds = useMemo(() => getRegionalOriginIds(user), [user]);
  const regionalSelectedOriginId = regionalOriginIds.length === 1
    ? regionalOriginIds[0]
    : filters.originId !== "all" && regionalOriginIds.includes(filters.originId)
      ? filters.originId
      : "all";
  const activeFilters = useMemo(() => ({
    ...filters,
    ...(isRegionalManager ? { originId: regionalSelectedOriginId, originIds: regionalOriginIds } : {}),
  }), [filters, isRegionalManager, regionalSelectedOriginId, regionalOriginIds]);
  const { records, loading } = useInfringementStream({
    tripId,
    filters: activeFilters,
    limitCount,
    enabled: true,
  });
  const { saveReview, savingId } = useInfringementReview(user);

  const speeding = records.filter((record) => record?.type === "speeding");
  const offline = records.filter((record) => record?.type === "offline");
  const pendingCount = records.filter((record) => (record?.reviewStatus || "pending") === "pending").length;

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  const saveActionLog = async (record, payload) => {
    if (!record?.id) return;
    setActionSavingId(record.id);
    try {
      await httpClient.post(Endpoints.LOG_INFRINGEMENT_ACTION(record.id), payload);
      toast.success("Driver action logged");
      setSelectedRecord(null);
    } catch (e) {
      console.error("[INFRINGEMENT ACTION] save failed", e);
      toast.error(e?.response?.data?.message || "Failed to log driver action");
    } finally {
      setActionSavingId(null);
    }
  };

  return <div className="flex flex-col gap-6">
    <div className="rounded-[28px] border border-[#ece5dd] bg-white px-6 py-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
      <div className="flex flex-col gap-2">
        <span className="text-[1.15rem] font-semibold text-[#161311]">{title}</span>
        <span className="text-sm text-[#6e6258]">Real-time Firestore feed for speeding and offline infringements.</span>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4 xl:grid-cols-4">
        <SummaryCard label="All Events" value={records.length} />
        <SummaryCard label="Speeding" value={speeding.length} accent="text-[#cf4b1f]" />
        <SummaryCard label="Offline" value={offline.length} accent="text-[#6b53d5]" />
        <SummaryCard label="Pending Review" value={pendingCount} accent="text-[#1f6bb8]" />
      </div>

      {showFilters ? <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-6">
        <Input placeholder="Filter driver" value={filters.driver} onChange={(event) => updateFilter("driver", event.target.value)} />
        <Input placeholder="Filter vehicle" value={filters.vehicle} onChange={(event) => updateFilter("vehicle", event.target.value)} />
        {!tripId ? <Input placeholder="Filter trip" value={filters.trip} onChange={(event) => updateFilter("trip", event.target.value)} /> : <div />}
        <Select
          value={activeFilters.originId}
          onChange={(value) => updateFilter("originId", value)}
          disabled={isRegionalManager && regionalOriginIds.length <= 1}
          options={[
            ...(isRegionalManager && regionalOriginIds.length <= 1 ? [] : [{ label: isRegionalManager ? "All assigned provinces" : "All provinces", value: "all" }]),
            ...DefaultOrigins
              .filter((origin) => !isRegionalManager || regionalOriginIds.includes(origin.id))
              .map((origin) => ({ label: origin.name, value: origin.id })),
          ]}
        />
        <Select
          value={filters.type}
          onChange={(value) => updateFilter("type", value)}
          options={[
            { label: "All types", value: "all" },
            { label: "Speeding", value: "speeding" },
            { label: "Offline", value: "offline" },
          ]}
        />
        <Select
          value={filters.status}
          onChange={(value) => updateFilter("status", value)}
          options={[
            { label: "All review states", value: "all" },
            { label: "Pending", value: "pending" },
            { label: "Accepted", value: "accepted" },
            { label: "Dismissed", value: "dismissed" },
            { label: "Uncertain", value: "uncertain" },
          ]}
        />
        <Select
          value={filters.confidence}
          onChange={(value) => updateFilter("confidence", value)}
          options={[
            { label: "All confidence", value: "all" },
            { label: "High", value: "high" },
            { label: "Medium", value: "medium" },
            { label: "Low", value: "low" },
          ]}
        />
        <DatePicker.RangePicker value={filters.dateRange} onChange={(value) => updateFilter("dateRange", value)} />
        <div className="xl:col-span-2 flex items-center justify-end">
          <Button onClick={() => setFilters(infringementDefaultFilters)}>Reset Filters</Button>
        </div>
      </div> : null}
    </div>

    <SpeedInfringementSettingsPanel />

    <InfringementActionLogPanel provinceId={activeFilters.originId} />

    {loading ? <div className="flex justify-center py-10"><Spin /></div> : <>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <SpeedingPanel records={speeding} onSelect={setSelectedRecord} />
        <OfflinePanel records={offline} onSelect={setSelectedRecord} />
      </div>

      <div className="rounded-[28px] border border-[#ece5dd] bg-white px-6 py-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
        <InfringementTable records={records} loading={loading} onSelect={setSelectedRecord} />
      </div>
    </>}

    <InfringementDetailModal
      open={Boolean(selectedRecord)}
      record={selectedRecord}
      onCancel={() => setSelectedRecord(null)}
      onReview={saveReview}
      onAction={saveActionLog}
      saving={savingId === selectedRecord?.id}
      actionSaving={actionSavingId === selectedRecord?.id}
      canReview={!isRegionalManager}
      canLogAction
      selectedProvinceId={activeFilters.originId}
    />
  </div>;
};
